import { useRouterVariables } from "@/components/routing-utils/useRouterVariables";
import { LoginContextProvider } from "@/flokit/utils/login";
import { useRouter } from "next/router";
import React from "react";
import { ROUTER_VARIABLES } from "../utils/router-variables";
import { URLS } from "../utils/urls";
import { useAmILoggedIn } from "./api-docs";

// Urls that will not be authenticated
// Used for non admin urls

const WorkAllocationUrls = [
  new RegExp(
    "^/app/[a-zA-Z0-9-]+/work-allocation/programs/[a-zA-Z0-9-]+/request-matter",
  ),
  new RegExp("^/app/[a-zA-Z0-9-]+/work-allocation/associate/.*"), // work allocation associate stuff,
  new RegExp("^/app/[a-zA-Z0-9-]+/work-allocation/programs/[0-9]+/.*"), // work allocation feedback portal
];

const MemberUrl = new RegExp("^/app/[a-zA-Z0-9-]+/member"); // any member page

const urls = [
  new RegExp("^/app/[a-zA-Z0-9-]+/login"), // login page
  new RegExp("^/app/[a-zA-Z0-9-]+/forgot-password"), // forgot password page
  new RegExp("^/app/[a-zA-Z0-9-]+/forgot-password/success.*"), // forgot password success page
  new RegExp("^/app/[a-zA-Z0-9-]+/create-account"), // account creation page
  new RegExp("^/app/[a-zA-Z0-9-]+/create-account/success.*"), // account creation success page
  new RegExp("^/app/[a-zA-Z0-9-]+/reset-password.*"), // reset password page
  new RegExp("^/app/playground"), // playground
  new RegExp("^/app/[a-zA-Z0-9-]+/registration/.*"), // Employer/Student registration pages serendipitous
  new RegExp("^/app/[a-zA-Z0-9-]+/events/serendipitous/[0-9]+/student/.*"), // Student video chat experience serendipitous
  new RegExp("^/app/[a-zA-Z0-9-]+/events/serendipitous/[0-9]+/employer/.*"), // Employer video chat experience serendipitous
  new RegExp("^/linkExpired"), // Link expired
  new RegExp("^/job-sign-in-expired"),
  new RegExp("^/app/[a-zA-Z0-9-]+/jobs/.*"), // careers page for ats
  new RegExp("^/app/[a-zA-Z0-9-]+/jobs"), // careers page for ats
  new RegExp("^/app/[a-zA-Z0-9-]+/candidate-review"), // candidate-review
  new RegExp("^/app/[a-zA-Z0-9-]+/candidate-review/.*"), // candidate-review
  new RegExp("^/app/[a-zA-Z0-9-]+/[a-zA-Z0-9-]+/jobs"),
  new RegExp("^/app/[a-zA-Z0-9-]+/events/preselect/[0-9]+/student/.*"), // Preselect student pages
  new RegExp("^/app/[a-zA-Z0-9-]+/events/preselect/[0-9]+/interviewer/.*"), // Preselect interviewer pages
  new RegExp("^/app/[a-zA-Z0-9-]+/events/lottery/[0-9]+/interviewer/.*"), // Lottery interviewer pages
  new RegExp("^/app/[a-zA-Z0-9-]+/events/lottery/[0-9]+/student/.*"), // Preselect student pages
  new RegExp("^/app/aspire"), // aspire
  new RegExp("^/app/forward"), // forward
  new RegExp("^/app/[a-zA-Z0-9-]+/performance/.*"), // performance
  ...WorkAllocationUrls,
  MemberUrl,
  // allow the home page, regardless of trailing slash
  new RegExp("^/app/{0,1}$"),
];

export const useAdminAuthNeeded = (path: string) => {
  let tempAuthNeeded = true;
  for (let i = 0; i < urls.length; i++) {
    if (path?.match(urls[i])) {
      tempAuthNeeded = false;
      break;
    }
  }

  return tempAuthNeeded;
};

const memberPathRegex = /^\/app\/([a-zA-Z0-9-]+)\/member/;

// Component redirects whenever user is not logged in
const AdminLoginGuard = (props: { children: React.ReactNode }) => {
  const router = useRouter();
  const asPath = router.asPath;
  const authNeeded = useAdminAuthNeeded(asPath);

  const { data, isLoading, isError } = useAmILoggedIn({
    query: { retry: 2 },
  });

  const { org: routeOrgFriendlyName } = useRouterVariables({
    [ROUTER_VARIABLES.org]: "string",
  });

  // Wait to load before making redirect decisions
  if (isLoading) {
    // TODO: I tried a loading mask, but spinner was appearing at the top
    // I don't quite understand why, because when I froze the loadingon resolving the amILoggedIn query, the loading spinner is centered. I'm not sure what else could be causing the off-center spinner
    return null;
  }

  const isAdmin = !!data?.success;
  const loggedInUserOrgFriendlyName = data?.orgFriendlyName ?? null;
  const routeOrgFriendlyNameValue = routeOrgFriendlyName.value;

  // If admin viewing member page, redirect them to corresponding member page
  const isMemberPath = MemberUrl.test(asPath);
  if (isAdmin && isMemberPath) {
    const adminPathName = asPath.replace(memberPathRegex, "/app/$1/admin");
    router.push(adminPathName);
    return null;
  }

  // If no auth needed, do nothing
  if (!authNeeded) {
    // Need to do this _after_ the member check to give chance to redirect
    return (
      <LoginContextProvider
        email={data?.email ?? null}
        userId={data?.uid ?? null}
      >
        {props.children}
      </LoginContextProvider>
    );
  }

  // If not an admin, redirect them
  if (!isAdmin || isError) {
    if (routeOrgFriendlyNameValue) {
      router.push(URLS.loginPageV2(routeOrgFriendlyNameValue));
    } else {
      // home page should already be allowed...
      if (asPath === "/app") {
        // ...so this branch shouldn't be hit
        return <>{props.children}</>;
      } else {
        router.push("/app");
      }
    }
    return null;
  }

  // If mismatching org, redirect them to login page for the route org
  const orgMismatch = loggedInUserOrgFriendlyName !== routeOrgFriendlyNameValue;
  if (!!routeOrgFriendlyNameValue && orgMismatch) {
    router.push(URLS.loginPageV2(routeOrgFriendlyNameValue));
    return null;
  }

  return (
    <LoginContextProvider
      email={data?.email ?? null}
      userId={data?.uid ?? null}
    >
      {props.children}
    </LoginContextProvider>
  );
};

export default AdminLoginGuard;
